import React, { memo, useRef, useContext } from 'react';
import { Formik } from 'formik';
import { useMoment } from '../../hooks';
import axios from 'axios';
import { getHeaders } from '../../request';
import HttpStatus from 'http-status-codes';
import validationSchema from './validationSchema';
import { estadoInicialForm } from './estadoInicialForm';
import { temPerfilRBAC } from '../../secutity/rbac';
import { TIPO_ABA_EXAME } from './constantes';
import {
    SttGrid,
    SttTranslateHook,
    SttCard,
    SttCardHeader,
    SttCardContent,
    SttCollapse
} from '@stt-componentes/core';
import { SttButton } from '@stt-componentes/core-v2';
import { MODALIDADE, PERFIL } from '../../common/Constants';
import { useSignalEffect, useSignals } from '@preact/signals-react/runtime';
import { filtros, listar, resetFiltros } from '../../signals/exame';
import alerta from '../../signals/alerta';
import usuario from '../../signals/usuario';
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { EXAME_PRIORIZADO } from './fieldNames';

import { setPesquisaAvancada, pesquisaAvancada } from '../../request/index';
import DataInicialExame from './campos/data-inicial-exame';
import DataFinalExame from './campos/data-final-exame';
import DataInicialLaudo from './campos/data-inicial-laudo';
import DataFinalLaudo from './campos/data-final-laudo';
import SituacaoLaudo from './campos/situacao-laudo';
import SituacaoExame from './campos/situacao-exame';
import Paciente from './campos/paciente';
import Uf from './campos/uf';
import Municipio from './campos/municipio';
import Instituicao from './campos/instituicao';
import Rede from './campos/rede';
import Requisicao from './campos/requisicao';
import Protocolo from './campos/protocolo';
import NumeroExame from './campos/numero-exame';
import ResponsavelEnvio from './campos/responsavel-envio';
import ExamePriorizado from './campos/exame-priorizado';
import MedicoExecutor from './campos/medico-executor';

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%'
    },
    cardAvancado: {
        marginTop: theme.spacing(1)
    },
    buttonWrapper: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(3),
    },
    containerExame: {
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between'
    }
}));

const FormPesquisa = (props) => {
    const {
        tipoAba,
        idAba,
        callbackBusca,
        buscaEmAndamento,
        page,
        count,
        laudador,
        resetPageCount,
        startBusca,
        user
    } = props;

    useSignals();

    const { strings } = useContext(SttTranslateHook.I18nContext);

    const schema = validationSchema(strings);
    const formRef = useRef();
    const moment = useMoment();
    const classes = useStyles();

    useSignalEffect(() => {
        if (listar.value && formRef.current) {
            formRef.current.handleSubmit();
        }
    })

    const gerarFiltroImplicito = (filtros) => {
        switch (tipoAba) {
            case TIPO_ABA_EXAME.INSTITUICAO:
                filtros.instituicao = idAba;
                break;

            case TIPO_ABA_EXAME.PERFIL:
                if (temPerfilRBAC(usuario, PERFIL.MEDICO_SOLICITANTE)) {
                    filtros.solicitante = usuario.value.idFuncionario;
                }
                break;
            case TIPO_ABA_EXAME.REDE:
                filtros.rede = idAba;
                if (temPerfilRBAC(usuario, PERFIL.MEDICO_LAUDADOR)) {
                    filtros.laudador = usuario.value.idFuncionario;
                }
                break;
        }
    }

    const estruturarFiltros = (dados) => {
        let filtrosPesquisa = {
            'sigla-modalidade': MODALIDADE.SIGLA,
            start: dados.start,
            count: dados.count
        };
        gerarFiltroImplicito(filtrosPesquisa);

        if (dados.uf) {
            filtrosPesquisa.uf = dados.uf.id;
        }
        if (dados.municipio) {
            filtrosPesquisa.municipio = dados.municipio.id;
        }
        if (dados.instituicao) {
            filtrosPesquisa.instituicao = dados.instituicao.id;
        }
        if (dados['funcionario-envio']) {
            filtrosPesquisa['funcionario-envio'] = dados['funcionario-envio'];
        }
        if (dados.requisicao) {
            filtrosPesquisa.requisicao = dados.requisicao;
        }
        if (dados.paciente) {
            filtrosPesquisa.paciente = dados.paciente;
        }
        if (dados.rede) {
            filtrosPesquisa.rede = dados.rede.id;
        }
        if (dados['cpf-paciente']) {
            filtrosPesquisa['cpf-paciente'] = dados['cpf-paciente'];
        }
        if (dados['cns-paciente']) {
            filtrosPesquisa['cns-paciente'] = dados['cns-paciente'];
        }
        if (dados['data-exame-de']) {
            filtrosPesquisa['data-exame-de'] = moment(dados['data-exame-de']).format('YYYY-MM-DD');
        }
        if (dados['data-exame-ate']) {
            filtrosPesquisa['data-exame-ate'] = moment(dados['data-exame-ate']).format('YYYY-MM-DD');
        }
        if (dados['data-laudo-de']) {
            filtrosPesquisa['data-laudo-de'] = moment(dados['data-laudo-de']).format('YYYY-MM-DD');
        }
        if (dados['data-laudo-ate']) {
            filtrosPesquisa['data-laudo-ate'] = moment(dados['data-laudo-ate']).format('YYYY-MM-DD');
        }
        if (dados['data-invalidacao-de']) {
            filtrosPesquisa['data-invalidacao-de'] = moment(dados['data-invalidacao-de']).format('YYYY-MM-DD');
        }
        if (dados['data-invalidacao-ate']) {
            filtrosPesquisa['data-invalidacao-ate'] = moment(dados['data-invalidacao-ate']).format('YYYY-MM-DD');
        }
        if (dados['situacao-laudo'].length > 0) {
            filtrosPesquisa['situacao-laudo'] = dados['situacao-laudo'].join('|');
        }
        if (dados.atrasado) {
            filtrosPesquisa.atrasado = dados.atrasado;
        }
        if (dados.protocolo) {
            filtrosPesquisa.protocolo = dados.protocolo;
        }
        if (dados['numero-exame']) {
            filtrosPesquisa['numero-exame'] = dados['numero-exame'];
        }
        if (dados.situacao.length > 0) {
            filtrosPesquisa.situacao = dados.situacao.join('|');
        }
        if (dados['medico-executor']) {
            filtrosPesquisa['medico-executor'] = dados['medico-executor'];
        }
        if (dados[EXAME_PRIORIZADO]) {
            filtrosPesquisa[EXAME_PRIORIZADO] = 'sim';
        }

        return filtrosPesquisa;
    }

    return (
        <>
            <Formik
                innerRef={formRef}
                initialValues={filtros.value}
                validationSchema={schema}
                onSubmit={(data, { setSubmitting }) => {
                    const dados = { ...data };
                    dados.start = startBusca.value;
                    dados.count = count.value;
                    dados.page = page.value;
                    setSubmitting(true);

                    buscaEmAndamento.value = true;
                    // Salva os parâmetros da pesquisa para manter estado do 
                    // formulário durante a navegação entre páginas
                    filtros.value = dados;

                    const params = estruturarFiltros(dados);

                    axios.get(`${global.gConfig.url_base_exames}/exames`, { params: params, headers: getHeaders() })
                        .then((response) => {
                            const dados = response.data.data;
                            callbackBusca(dados);
                        })
                        .catch(err => {
                            callbackBusca({
                                itens: [],
                                totalRegistros: 0
                            });
                            const { response } = err;
                            let tituloAlertaPesquisa = '';
                            let mensagemAlertaPesquisa = '';
                            let msg = strings.mensagemErroGeral;
                            if (response) {
                                if (response.status === HttpStatus.BAD_REQUEST && response.data?.errors) {
                                    const erro = response.data;
                                    let arrMensagem = [];
                                    erro.errors.forEach(error => {
                                        arrMensagem.push(`- ${error.message}`);
                                    });
                                    msg = arrMensagem.join('\n');
                                    tituloAlertaPesquisa = erro.message;
                                    mensagemAlertaPesquisa = msg;
                                } else {
                                    tituloAlertaPesquisa = strings.erro;
                                    mensagemAlertaPesquisa = msg;
                                }
                            } else {
                                tituloAlertaPesquisa = strings.erro;
                                mensagemAlertaPesquisa = msg;
                            }
                            alerta.value = {
                                titulo: tituloAlertaPesquisa,
                                tipo: 'error',
                                open: true,
                                mensagem: mensagemAlertaPesquisa,
                                options: [
                                    {
                                        title: strings.ok,
                                        onClick: () => {
                                            alerta.value = { ...alerta.value, open: false }
                                        }

                                    }
                                ],
                                onClose: () => {
                                    alerta.value = { ...alerta.value, open: false }
                                }

                            }
                        })
                        .finally(() => {
                            setSubmitting(false);
                            buscaEmAndamento.value = false;
                        });
                }}
            >
                {
                    ({
                        isSubmitting,
                        handleSubmit,
                        resetForm
                    }) => {
                        return (
                            <>
                                <form onSubmit={handleSubmit} noValidate>
                                    <SttGrid container>
                                        <SttGrid item xs={12}>
                                            <SttCard variant="outlined" className={classes.card}>
                                                <SttCardHeader subheader={strings.exame} />
                                                <SttCardContent>
                                                    <SttGrid container spacing={1}>
                                                        <SttGrid item xs={12} sm={4} md={3}>
                                                            <DataInicialExame strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={3}>
                                                            <DataFinalExame strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={3}>
                                                            <DataInicialLaudo strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={3}>
                                                            <DataFinalLaudo strings={strings} />
                                                        </SttGrid>
                                                    </SttGrid>
                                                    <SttGrid>
                                                        <SttGrid className={classes.containerExame} container spacing={1}>
                                                            <SttGrid style={{ marginLeft: '5px' }}>
                                                                <SituacaoLaudo strings={strings} />
                                                            </SttGrid>
                                                            <SttGrid>
                                                                <SituacaoExame strings={strings} />
                                                            </SttGrid>
                                                        </SttGrid>
                                                    </SttGrid>
                                                </SttCardContent>
                                            </SttCard>
                                        </SttGrid>
                                    </SttGrid>
                                    <SttGrid item xs={12} >
                                        <Paciente strings={strings} />
                                    </SttGrid>
                                    <SttGrid className={classes.cardAvancado}>
                                        <SttCollapse in={pesquisaAvancada.value}>
                                            <SttCard variant="outlined" className={classes.card}>
                                                <SttCardHeader subheader={strings.pesquisaAvancada} />
                                                <SttCardContent>
                                                    <SttGrid container spacing={1}>
                                                        {temPerfilRBAC(usuario, [PERFIL.ADMINISTRADOR, PERFIL.ADMINISTRADOR_ESTADUAL]) && (
                                                            <SttGrid item xs={12} sm={5} md={3} lg={2}>
                                                                <MedicoExecutor strings={strings} />
                                                            </SttGrid>
                                                        )}
                                                        <SttGrid item xs={12} sm={5} md={3} lg={1}>
                                                            <Uf strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={5} md={3} lg={3}>
                                                            <Municipio strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={12} md={3} lg={4}>
                                                            <Instituicao strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={6} md={6} lg={2}>
                                                            <Rede strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={2}>
                                                            <Requisicao strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={2}>
                                                            <Protocolo strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={2}>
                                                            <NumeroExame strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid item xs={12} sm={4} md={2}>
                                                            <ResponsavelEnvio strings={strings} />
                                                        </SttGrid>
                                                        <SttGrid style={{ marginTop: '12px', marginLeft: '5px' }}>
                                                            <ExamePriorizado strings={strings} />
                                                        </SttGrid>
                                                    </SttGrid>
                                                </SttCardContent>
                                            </SttCard>
                                        </SttCollapse>
                                    </SttGrid>
                                    <SttGrid container spacing={3} className={classes.buttonWrapper}>
                                        <SttGrid item xs={12}>
                                            <SttButton
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={isSubmitting}
                                                nomarginleft="true"
                                                onClick={resetPageCount}
                                            >
                                                {strings.pesquisar}
                                            </SttButton>
                                            <SttButton
                                                type="button"
                                                variant="outlined"
                                                color="primary"
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                    // reset();
                                                    resetFiltros()
                                                    resetPageCount();
                                                    resetForm({
                                                        values: estadoInicialForm(global.gConfig)
                                                    });
                                                    callbackBusca(null);
                                                }}
                                            >
                                                {strings.limpar}
                                            </SttButton>
                                            <SttButton
                                                type="button"
                                                variant="outlined"
                                                color="secondary"
                                                onClick={setPesquisaAvancada}
                                            >
                                                {strings.pesquisaAvancada}
                                                {pesquisaAvancada ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </SttButton>
                                        </SttGrid>
                                    </SttGrid>
                                </form>
                            </>
                        )
                    }
                }
            </Formik>
        </>
    );
};

export default FormPesquisa;